// Pure helpers for the Matrix component family. Extracted to module scope to
// keep per-component cognitive complexity below SonarCloud's S3776 threshold
// (15) and to satisfy S7721 (no helpers defined inside the render body).

import type { IMetricThresholdSet, MatrixFormat, MetricTierColor } from '@skystone/shared';
import { classify } from '@skystone/shared';

/**
 * Tier colour token returned for each matrix cell. The Matrix uses a tri-state
 * palette (success/warning/error); `null` means "no tier matched" or "no
 * value to classify" — rendered as a neutral cell.
 */
export type MatrixCellTone = 'success' | 'warning' | 'error' | null;

/**
 * Map a tier's `colorToken` onto the three matrix tones. The classifier
 * itself supports four tones (success/primary/warning/error — the DORA Elite
 * tier uses `primary`); the QC matrices in this phase only use the three
 * standard semantic tones, so we treat `primary` as `success` for cell
 * shading. This matches the mockup's green/amber/red banding.
 */
function tokenToTone(token: MetricTierColor): Exclude<MatrixCellTone, null> {
  switch (token) {
    case 'success':
    case 'primary':
      return 'success';
    case 'warning':
      return 'warning';
    case 'error':
      return 'error';
  }
}

/**
 * Classify a possibly-null value into one of the three matrix tones.
 * Returns `null` for null inputs or when no tier matches.
 */
export function classifyCellTone(
  value: number | null,
  thresholds: IMetricThresholdSet,
): MatrixCellTone {
  if (value === null) return null;
  const tierId = classify(value, thresholds);
  if (!tierId) return null;
  const tier = thresholds.tiers.find((t) => t.tierId === tierId);
  if (!tier) return null;
  return tokenToTone(tier.colorToken);
}

/**
 * Background class for a regular (non-org) matrix cell, based on its tone.
 * Uses low-opacity semantic tokens so the navy text remains legible.
 */
export function cellBackgroundClass(tone: MatrixCellTone): string {
  switch (tone) {
    case 'success':
      return 'bg-success/15';
    case 'warning':
      return 'bg-warning/15';
    case 'error':
      return 'bg-error/15';
    case null:
      return '';
  }
}

/**
 * Background class for an org-row matrix cell. The org row sits on a navy
 * background, so cells use a higher-opacity semantic colour to read against
 * the dark surface.
 */
export function orgCellBackgroundClass(tone: MatrixCellTone): string {
  switch (tone) {
    case 'success':
      return 'bg-success/55';
    case 'warning':
      return 'bg-warning/55';
    case 'error':
      return 'bg-error/55';
    case null:
      return '';
  }
}

/**
 * SVG stroke colour for a sparkline drawn against the regular row
 * background. We use the CSS variables backing the Tailwind semantic tokens
 * so the stroke updates with theme.
 */
export function sparklineStroke(tone: MatrixCellTone): string {
  switch (tone) {
    case 'success':
      return 'var(--color-success)';
    case 'warning':
      return 'var(--color-warning)';
    case 'error':
      return 'var(--color-error)';
    case null:
      return 'currentColor';
  }
}

const FORMAT_SUFFIX: Record<MatrixFormat, string> = {
  percent: '%',
  days: 'd',
  'per-week': '/wk',
  hours: 'h',
  count: '',
};

/** Format a non-null numeric value for display in a matrix cell. */
export function formatValue(value: number, format: MatrixFormat): string {
  return `${value}${FORMAT_SUFFIX[format]}`;
}

/**
 * Direction marker for the Δ column. `null` => no previous OR no current,
 * therefore neutral em-dash.
 */
export type DeltaDirection = 'up' | 'down' | 'flat' | null;

export interface DeltaDescriptor {
  direction: DeltaDirection;
  /** Absolute change as a formatted string (no sign), or '0' for flat / '—' for null. */
  text: string;
  /** True when the change is in the favourable direction for the metric. */
  favourable: boolean;
}

/**
 * Compare two adjacent column values and produce the Δ rendering hints.
 * `lowerBetter` is sourced from the threshold set (`direction === 'lower_better'`).
 */
export function describeDelta(
  previous: number | null,
  current: number | null,
  lowerBetter: boolean,
  format: MatrixFormat,
): DeltaDescriptor {
  if (previous === null || current === null) {
    return { direction: null, text: '—', favourable: false };
  }
  // Round the difference to collapse binary floating-point error — e.g.
  // 4.4 - 6.1 = -1.6999999999999993, which would otherwise render as a very
  // long number that stretches the Δ column and knocks the matrices out of
  // horizontal alignment. Cell values are pre-rounded to at most one decimal
  // upstream, so 1e6 preserves every real digit while removing the ~1e-15
  // noise. It also lets a noise-only difference collapse to the flat state.
  const diff = Math.round((current - previous) * 1e6) / 1e6;
  if (diff === 0) {
    return { direction: 'flat', text: '0', favourable: false };
  }
  const direction: DeltaDirection = diff > 0 ? 'up' : 'down';
  const favourable = lowerBetter ? diff < 0 : diff > 0;
  return {
    direction,
    text: formatValue(Math.abs(diff), format),
    favourable,
  };
}

/**
 * Sort key used to put the "best" team on top. We sort by the most recent
 * column value (rightmost), with nulls always sinking to the bottom.
 *
 * Returns a comparator suitable for Array.prototype.sort.
 */
export function sortRowsByLatest<T extends { values: (number | null)[] }>(
  rows: readonly T[],
  lowerBetter: boolean,
): T[] {
  const lastIndex = rows[0]?.values.length ? rows[0].values.length - 1 : 0;
  return [...rows].sort((a, b) => {
    const av = a.values[lastIndex];
    const bv = b.values[lastIndex];
    if (av === null && bv === null) return 0;
    if (av === null) return 1; // null sinks
    if (bv === null) return -1;
    return lowerBetter ? av - bv : bv - av;
  });
}
